import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Mail, Copy, X } from "lucide-react";
import { api } from "../lib/api";
import { euro, fmtDate, daysLeft } from "../lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const SCENARIOS = [
  { key: "verifica", label: "Verifica Ricezione (Mai aperto)" },
  { key: "followup", label: "Follow-up Consulenziale (Visualizzato / Caldo 🔥)" },
  { key: "scadenza", label: "Avviso Scadenza Imminente (-48h)" },
  { key: "proroga", label: "Offerta Proroga / Recupero Scaduto" },
  { key: "acconto", label: "Sollecito Versamento Acconto" },
];

function autoScenario(q) {
  if (q.stato_pagamento_acconto === "in_attesa") return "acconto";
  if (q.status === "scaduto") return "proroga";
  const dl = daysLeft(q.due_date);
  if ((q.status === "inviato" || q.status === "visualizzato") && dl !== null && dl <= 2) return "scadenza";
  if (q.status === "visualizzato" || (q.totale_visite || 0) >= 3) return "followup";
  return "verifica";
}

function buildMessage(scenario, tone, v) {
  const f = tone === "formale";
  const hi = f ? `Gentile ${v.name},` : `Ciao ${v.name},`;
  const you = f ? "La" : "ti";
  const your = f ? "Suo" : "tuo";
  const bye = f ? "Cordiali saluti" : "A presto";
  const L = `\n\n👉 ${v.link}`;
  switch (scenario) {
    case "verifica":
      return `${hi}\n\n${f ? "Le" : "ti"} ho inviato il preventivo "${v.title}" e volevo assicurarmi che ${f ? "Le" : "ti"} sia arrivato correttamente.${L}\n\nResto a disposizione per qualsiasi chiarimento.\n\n${bye}`;
    case "followup":
      return `${hi}\n\nho visto che ha dato un'occhiata al preventivo "${v.title}". ${f ? "Desidera" : "Vuoi"} approfondire qualche pacchetto o valutare insieme gli add-on opzionali?${L}\n\nSono qui per aiutar${you}.\n\n${bye}`;
    case "scadenza":
      return `${hi}\n\n${f ? "Le ricordo" : "ti ricordo"} che la validità del preventivo "${v.title}"${v.promo ? ` (e del codice promo ${v.promo})` : ""} scade il ${v.due}. ${f ? "La invito" : "ti invito"} a confermare per bloccare le condizioni attuali.${L}\n\n${bye}`;
    case "proroga":
      return `${hi}\n\nbuone notizie: ho prorogato eccezionalmente la validità del preventivo "${v.title}" fino al ${v.due}. ${f ? "Può" : "Puoi"} rivederlo e accettarlo qui:${L}\n\n${bye}`;
    case "acconto":
      return `${hi}\n\ngrazie per aver accettato il preventivo "${v.title}"! Per avviare i lavori resta da versare l'acconto di ${v.deposit}.\n\nRiepilogo del preventivo:${L}\n\n${bye}`;
    default:
      return "";
  }
}

export default function FollowupModal({ quote, onClose, onSent }) {
  const open = !!quote;
  const [scenario, setScenario] = useState(() => (quote ? autoScenario(quote) : "verifica"));
  const [tone, setTone] = useState("formale");
  const [edited, setEdited] = useState(null);

  const vars = useMemo(() => {
    if (!quote) return {};
    return {
      name: quote.client?.name || "Cliente",
      title: quote.title || "",
      netto: euro(quote.totals?.netto ?? quote.totals?.total ?? 0, quote.currency || "EUR"),
      due: fmtDate(quote.due_date),
      link: `${window.location.origin}/p/${quote.public_token}`,
      promo: quote.codice_promo || "",
      deposit: euro(quote.deposit?.amount ?? 0, quote.currency || "EUR"),
    };
  }, [quote]);

  const generated = useMemo(() => buildMessage(scenario, tone, vars), [scenario, tone, vars]);
  const message = edited != null ? edited : generated;

  React.useEffect(() => {
    setScenario(quote ? autoScenario(quote) : "verifica");
    setEdited(null);
  }, [quote]);
  React.useEffect(() => {
    setEdited(null);
  }, [scenario, tone]);

  if (!quote) return null;

  const scenarioLabel = SCENARIOS.find((s) => s.key === scenario)?.label || "Follow-up";

  const logSend = (channel) => {
    api.post(`/quotes/${quote.id}/log-reminder`, { scenario: scenarioLabel, channel }).then(() => onSent?.());
  };

  const sendWhatsApp = () => {
    const phone = (quote.client?.phone || "").replace(/[^\d]/g, "");
    if (!phone) return toast.error("Numero di telefono del cliente mancante");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
    logSend("WhatsApp");
  };
  const sendEmail = () => {
    const email = quote.client?.email || "";
    if (!email) return toast.error("Email del cliente mancante");
    const subject = `${quote.title} — ${quote.quote_number}`;
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`, "_blank");
    logSend("Email");
  };
  const copyText = () => {
    navigator.clipboard.writeText(message);
    toast.success("Testo copiato negli appunti!");
    logSend("Copia");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="followup-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send style={{ width: 18, height: 18 }} className="text-primary" /> Follow-up / Sollecito
          </DialogTitle>
          <DialogDescription>{quote.quote_number} · {quote.client?.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Scenario</p>
            <Select value={scenario} onValueChange={setScenario}>
              <SelectTrigger data-testid="followup-scenario-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCENARIOS.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Tono di voce</p>
            <div className="flex gap-2">
              <button
                data-testid="followup-tone-formale"
                onClick={() => setTone("formale")}
                className={`flex-1 h-9 rounded-lg text-sm font-semibold border transition-colors ${tone === "formale" ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary"}`}
              >
                Formale (Lei)
              </button>
              <button
                data-testid="followup-tone-informale"
                onClick={() => setTone("informale")}
                className={`flex-1 h-9 rounded-lg text-sm font-semibold border transition-colors ${tone === "informale" ? "bg-primary text-white border-primary" : "border-slate-200 text-slate-600 hover:border-primary"}`}
              >
                Informale (Tu)
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Anteprima messaggio</p>
            <Textarea
              data-testid="followup-message-editor"
              value={message}
              onChange={(e) => setEdited(e.target.value)}
              rows={9}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-2 pt-1">
            <Button data-testid="followup-send-whatsapp" onClick={sendWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 font-semibold">
              <MessageCircle style={{ width: 15, height: 15 }} className="mr-1.5" /> WhatsApp
            </Button>
            <Button data-testid="followup-send-email" onClick={sendEmail} className="bg-blue-600 hover:bg-blue-700 font-semibold">
              <Mail style={{ width: 15, height: 15 }} className="mr-1.5" /> Email
            </Button>
            <Button data-testid="followup-copy-text" onClick={copyText} variant="outline" className="font-semibold">
              <Copy style={{ width: 15, height: 15 }} className="mr-1.5" /> Copia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
